import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { ExpenseService } from '@/effects/expenses/expense.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import type { PaginatedExpenses, Expense as ApiExpense } from '@/api/schemas/expense.schema';
import type { Expense } from '@/types/domain.types';

// Transform API expense to domain expense
function transformExpense(apiExpense: ApiExpense): Expense {
  return {
    id: apiExpense.id,
    group_id: apiExpense.group_id,
    category_id: apiExpense.category_id,
    description: apiExpense.description || '',
    amount: apiExpense.amount || 0,
    currency: apiExpense.currency_code,
    currency_code: apiExpense.currency_code,
    paid_by_user_id: apiExpense.paid_by,
    paid_by: apiExpense.paid_by,
    created_by: apiExpense.created_by,
    date: apiExpense.created_at,
    created_at: apiExpense.created_at,
    user: apiExpense.user,
    category: apiExpense.category,
    weights: apiExpense.weights,
    synced: apiExpense.synced
  } as Expense;
}

export function useExpenses(groupId: string) {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.EXPENSES, groupId],
    queryFn: async ({ pageParam }) => {
      const program = pipe(
        ExpenseService,
        Effect.flatMap((service) =>
          service.fetchForGroup(groupId, pageParam)
        ),
        Effect.provide(MainLayer)
      );

      const result = await runEffect(program);
      return {
        ...result,
        expenses: result.expenses.map(transformExpense)
      };
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.nextCursor,
    initialPageParam: undefined as string | undefined,
    staleTime: 30_000, // 30 seconds
    enabled: !!groupId,
  });
}

export function useExpensesPrefetch(groupId: string) {
  const queryClient = useQueryClient();

  return () => {
    if (!groupId) return;

    queryClient.prefetchInfiniteQuery({
      queryKey: [QUERY_KEYS.EXPENSES, groupId],
      queryFn: async () => {
        const program = pipe(
          ExpenseService,
          Effect.flatMap((service) => service.fetchForGroup(groupId)),
          Effect.provide(MainLayer)
        );

        return runEffect(program);
      },
      initialPageParam: undefined,
    });
  };
}

// Hook to get all expenses as a flat array
export function useExpensesFlat(groupId: string) {
  const query = useExpenses(groupId);

  return {
    ...query,
    data: query.data?.pages.flatMap((page) => page.expenses) ?? [],
  };
}

// Hook to get expense details (if we need individual expense fetching)
export function useExpenseDetails(expenseId: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [QUERY_KEYS.EXPENSE_DETAILS, expenseId],
    queryFn: async () => {
      // This would require an endpoint to fetch individual expense
      // For now, we'll get it from the cache
      const allExpenses = queryClient.getQueryData([QUERY_KEYS.EXPENSES]) as {
        pages: PaginatedExpenses[]
      } | undefined;
      return allExpenses?.pages
        ?.flatMap((page) => page.expenses)
        ?.find((expense) => expense.id === expenseId);
    },
    enabled: !!expenseId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
